import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Switch from 'react-switch';
import { ReactSVG } from 'react-svg';

import downCaretIcon from '../../images/icons/down-caret.svg';
import leftAngleIcon from '../../images/icons/left-angle.svg';
import rightAngleIcon from '../../images/icons/right-angle.svg';
import bothDirectionsIcon from '../../images/icons/journey/both-directions.svg';
import externalDirectionIcon from '../../images/icons/journey/external-direction.svg';
import internalDirectionIcon from '../../images/icons/journey/internal-direction.svg';
import globeIcon from '../../images/icons/journey/globe.svg';
import plusIcon from '../../images/icons/plus-sign.svg';
import pencilIcon from '../../images/icons/screens/pencil.svg';
import trashIcon from '../../images/icons/analytics/trash.svg';
import checkIcon from '../../images/icons/check.svg';
import closeIcon from '../../images/icons/screens/close.svg';
import walkModeIcon from '../../images/icons/modes/walk.svg';
import carModeIcon from '../../images/icons/modes/car.svg';
import ridehailModeIcon from '../../images/icons/modes/ridehail.svg';
import shuttleModeIcon from '../../images/icons/modes/shuttle.svg';
import busModeIcon from '../../images/icons/modes/bus.svg';
import subwayModeIcon from '../../images/icons/modes/subway.svg';
import trainModeIcon from '../../images/icons/modes/train.svg';
import tramModeIcon from '../../images/icons/modes/tram.svg';
import ferryModeIcon from '../../images/icons/modes/ferry.svg';
import bikeModeIcon from '../../images/icons/modes/bike.svg';
import bikeshareModeIcon from '../../images/icons/modes/bikeshare.svg';
import scooterModeIcon from '../../images/icons/modes/scooter.svg';

// Fields every SublocationRule needs beyond type/config/sublocationId/playlistId, defaulted for
// a freshly-created row.
const NEW_RULE_BASE_FIELDS = {
    direction: null,
    interval: null,
    relevantModes: null,
    timeFrom: null,
    timeTo: null,
    days: null,
    dateFrom: null,
    dateTo: null,
};

// Order matches the day picker in the Repeat field (Sunday first).
const WEEKDAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const WEEKDAY_FULL_LABEL = {
    sunday: 'Sunday', monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
    thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday',
};
const WEEKDAYS_ONLY = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const WEEKEND_ONLY = ['saturday', 'sunday'];

const EMPTY_DRAFT = {
    isActive: true,
    dateFrom: '',
    dateTo: '',
    timeFrom: '',
    timeTo: '',
    days: [...WEEKDAY_ORDER],
    direction: '',
    label: '',
    secondaryValue: '',
};

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

// A rule with no `days` at all means "every day" (same as all 7 being picked) — everything else
// collapses to exactly one of these four labels, no matter which specific days are checked. An
// explicitly empty selection (every day unchecked) is the opposite of "every day" — Custom, not Daily.
const formatRepeat = (days) => {
    if (!days || days.length === 7) return 'Daily';
    if (days.length === 0) return 'Custom';
    const daySet = new Set(days);
    if (daySet.size === 5 && WEEKDAYS_ONLY.every(d => daySet.has(d))) return 'Weekdays';
    if (daySet.size === 2 && WEEKEND_ONLY.every(d => daySet.has(d))) return 'Weekends';
    return 'Custom';
};

const formatTime = (value) => {
    if (!value) return '-';
    const [hourStr, minuteStr] = value.split(':');
    let hour = parseInt(hourStr, 10);
    if (isNaN(hour)) return value;
    const period = hour >= 12 ? 'pm' : 'am';
    hour = hour % 12 || 12;
    return `${hour}:${minuteStr ?? '00'}${period}`;
};

// A "date" field can arrive as either a plain date ("2026-08-24") or a full ISO datetime — pull
// the calendar-day parts straight out of the string so this never shifts a day from timezone
// conversion (new Date("2026-08-24") is parsed as UTC midnight, which can print as the prior day
// in local getters).
const parseDateParts = (value) => {
    if (!value) return null;
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (isoMatch) {
        return { year: parseInt(isoMatch[1], 10), month: parseInt(isoMatch[2], 10) - 1, day: parseInt(isoMatch[3], 10) };
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    return { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() };
};

// Same reasoning as parseDateParts above — this must not go through new Date(value) + local
// getters, or it prints a day earlier than what was actually saved in negative-UTC-offset zones.
const formatDateParts = (parts) => {
    const pad = n => String(n).padStart(2, '0');
    return `${pad(parts.month + 1)}/${pad(parts.day)}/${parts.year}`;
};

const formatTurnDate = (value) => {
    const parts = parseDateParts(value);
    return parts ? formatDateParts(parts) : '-';
};

const compareDateParts = (a, b) => (a.year - b.year) || (a.month - b.month) || (a.day - b.day);

// Shared by the Type pill, the "Rule type" filter, and the calendar underline below, so they can
// never disagree. A rule with no dates at all is Continuous; a dated rule is Expired specifically
// once today is past its Turn off date (dateTo) — a dated rule that just hasn't started yet is
// still plain Dated.
const getRuleTypeCategory = (rule, todayParts) => {
    const isDated = !!(rule.dateFrom || rule.dateTo);
    if (!isDated) return 'continuous';
    const dateTo = parseDateParts(rule.dateTo);
    if (dateTo && compareDateParts(todayParts, dateTo) > 0) return 'expired';
    return 'dated';
};

// A Dated rule with only a Turn on date (no Turn off) runs forever from that date; one with only
// a Turn off date (no Turn on) has always been running up to that date — either bound is open-
// ended when absent, not collapsed down to a single day matching the bound that IS set.
const isWithinOpenRange = (dateParts, from, to) =>
    (!from || compareDateParts(dateParts, from) >= 0) && (!to || compareDateParts(dateParts, to) <= 0);

// No `days` at all (or all 7) means "every day of the week" — same convention formatRepeat uses.
// Otherwise the rule only applies on the specific weekdays it lists, regardless of whether it's
// Continuous or Dated — a Continuous rule isn't necessarily every single day if it repeats.
const ruleAppliesOnWeekday = (rule, year, month, day) => {
    if (!rule.days || rule.days.length === 0 || rule.days.length === 7) return true;
    const weekday = WEEKDAY_ORDER[new Date(year, month, day).getDay()];
    return rule.days.includes(weekday);
};

// The underline color matches the Type pill exactly (via getRuleTypeCategory) — Dated is amber,
// Continuous is green, Expired is red. A Dated (or Expired) rule only underlines the days its
// range covers (open-ended on whichever side has no date set) that also match its repeat days; a
// Continuous rule underlines every day in the month UNLESS it also has repeat days set, in which
// case only those weekdays get underlined. Rules of the same type can't overlap, so there's no
// risk of two rules claiming the same day.
const buildDateHighlights = (rules, year, month) => {
    const highlights = {};
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const todayParts = { year: today.getFullYear(), month: today.getMonth(), day: today.getDate() };

    rules.forEach(rule => {
        const color = getRuleTypeCategory(rule, todayParts);

        if (color === 'continuous') {
            for (let day = 1; day <= daysInMonth; day++) {
                if (ruleAppliesOnWeekday(rule, year, month, day)) {
                    highlights[day] = { underline: color };
                }
            }
            return;
        }

        const from = parseDateParts(rule.dateFrom);
        const to = parseDateParts(rule.dateTo);

        for (let day = 1; day <= daysInMonth; day++) {
            const current = { year, month, day };
            if (isWithinOpenRange(current, from, to) && ruleAppliesOnWeekday(rule, year, month, day)) {
                highlights[day] = { underline: color };
            }
        }
    });

    return highlights;
};

// Despite the name, this checks whether ANY given date falls within a rule's range AND on one of
// its repeat days — a Continuous rule with no repeat days always matches, a Dated one matches the
// days its range covers (open-ended on whichever side has no date set — see isWithinOpenRange
// above). Used both for "is this rule in effect today" and for the calendar's click-a-date filter
// below, and mirrors the same logic buildDateHighlights uses so this always agrees with what the
// calendar underline shows.
const isDatedRuleCurrentlyInRange = (rule, dateParts) => {
    if (!ruleAppliesOnWeekday(rule, dateParts.year, dateParts.month, dateParts.day)) return false;
    const from = parseDateParts(rule.dateFrom);
    const to = parseDateParts(rule.dateTo);
    if (!from && !to) return true;
    return isWithinOpenRange(dateParts, from, to);
};

// Always pads to a fixed 6 weeks (42 cells), even though most months only need 5 — otherwise a
// 6-week month would be taller than a 5-week one, misaligning the two calendars against each
// other and pushing the rules table down whenever you navigate to one.
const buildMonthGrid = (year, month) => {
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [...Array(firstWeekday).fill(null), ...Array(daysInMonth)].map((_, i) => i < firstWeekday ? null : i - firstWeekday + 1);
    while (cells.length < 42) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
};

// Every day is clickable — selecting one filters the rules table (below) down to whatever
// applies on that date, continuous rules included. `selectedDate` just controls the highlight
// circle here; the actual filtering happens where typeRules is computed.
const MiniCalendar = ({ year, month, highlights, selectedDate, onDayClick }) => {
    const weeks = buildMonthGrid(year, month);
    return (
        <div className="mini-calendar">
            <div className="mini-calendar-title">{MONTH_NAMES[month]} {year}</div>
            <div className="mini-calendar-weekdays">
                {WEEKDAY_LABELS.map(day => <span key={day}>{day}</span>)}
            </div>
            <div className="mini-calendar-grid">
                {weeks.map((week, weekIdx) => (
                    <div className="mini-calendar-week" key={weekIdx}>
                        {week.map((day, dayIdx) => {
                            const info = day ? (highlights[day] || {}) : {};
                            const isSelected = day && selectedDate
                                && selectedDate.year === year && selectedDate.month === month && selectedDate.day === day;
                            return (
                                <div
                                    key={dayIdx}
                                    className={`mini-calendar-day${!day ? ' empty' : ''}${isSelected ? ' mini-calendar-day--selected' : ''}`}
                                    onClick={day ? () => onDayClick(year, month, day) : undefined}
                                >
                                    {day && (
                                        <>
                                            <span className="day-number">{day}</span>
                                            {info.underline && <span className={`day-underline day-underline--${info.underline}`} />}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Without `options`, this just renders the static pill (used by the filters that aren't wired up
// yet). With `options`, it becomes a real dropdown: click to open, click an option to select it.
// `compact` renders it at a smaller, table-cell-friendly size (same design, same text, just smaller).
// The menu itself is portaled to <body> and positioned with fixed coordinates from the trigger's
// bounding rect — it needs to escape the rules table's scroll container, which otherwise clips
// (and forces a scrollbar around) any absolutely-positioned child that overflows it vertically.
const FilterSelect = ({ icon, value, options, selectedValue, onChange, compact }) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const triggerRef = useRef(null);
    const isInteractive = !!options;

    useEffect(() => {
        if (!open) return;
        const closeMenu = () => setOpen(false);
        document.addEventListener('click', closeMenu);
        return () => document.removeEventListener('click', closeMenu);
    }, [open]);

    const handleToggle = () => {
        if (!open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            // minWidth, not width — a narrow (e.g. compact) trigger shouldn't force its options
            // to wrap; the menu can grow wider than the trigger to fit its longest label.
            setMenuStyle({ position: 'fixed', left: rect.left, minWidth: rect.width, top: rect.bottom + 4 });
        }
        setOpen(prev => !prev);
    };

    return (
        <div
            ref={triggerRef}
            className={`filter-select${isInteractive ? ' filter-select--interactive' : ''}${compact ? ' filter-select--compact' : ''}`}
            onClick={isInteractive ? handleToggle : undefined}
        >
            {icon && <ReactSVG className="filter-select-icon" src={icon} />}
            <span className="filter-select-value">{value}</span>
            <ReactSVG className="filter-select-caret" src={downCaretIcon} />
            {isInteractive && open && menuStyle && createPortal(
                <div className={`filter-select-menu${compact ? ' filter-select-menu--compact' : ''}`} style={menuStyle}>
                    {options.map(opt => (
                        <div
                            key={opt.value}
                            className={`filter-select-option${opt.value === selectedValue ? ' filter-select-option--active' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange(opt.value);
                                setOpen(false);
                            }}
                        >
                            {opt.icon && <ReactSVG className="filter-select-option-icon" src={opt.icon} />}
                            {opt.label}
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

// The Repeat field's own dropdown — a checklist of days instead of single-select options, so it
// doesn't close on a pick the way FilterSelect does. Shares FilterSelect's floating/portaled
// positioning approach so it isn't clipped by the rules table's scroll container either.
const RepeatDaySelect = ({ days, onChange, compact }) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    // Listens on mousedown rather than click purely so the outside-click check always fires
    // before any click-driven state update inside the menu has a chance to re-render things.
    useEffect(() => {
        if (!open) return;
        const closeMenu = (e) => {
            if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', closeMenu);
        return () => document.removeEventListener('mousedown', closeMenu);
    }, [open]);

    const handleToggleOpen = () => {
        if (!open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuStyle({ position: 'fixed', left: rect.left, minWidth: rect.width, top: rect.bottom + 4 });
        }
        setOpen(prev => !prev);
    };

    const toggleDay = (day) => {
        onChange(days.includes(day) ? days.filter(d => d !== day) : [...days, day]);
    };

    return (
        <div
            ref={triggerRef}
            className={`filter-select filter-select--interactive${compact ? ' filter-select--compact' : ''}`}
            onClick={handleToggleOpen}
        >
            <span className="filter-select-value">{formatRepeat(days)}</span>
            <ReactSVG className="filter-select-caret" src={downCaretIcon} />
            {open && menuStyle && createPortal(
                <div
                    ref={menuRef}
                    className={`filter-select-menu repeat-day-menu${compact ? ' filter-select-menu--compact' : ''}`}
                    style={menuStyle}
                    // A portal still bubbles its SYNTHETIC events up through the React tree (not
                    // the real DOM tree it's rendered into) — without this, a checkbox click here
                    // bubbles to the trigger div's own onClick above and immediately re-toggles
                    // `open` closed right after the checkbox's own onChange runs.
                    onClick={(e) => e.stopPropagation()}
                >
                    {WEEKDAY_ORDER.map(day => (
                        <label key={day} className="repeat-day-option">
                            <input type="checkbox" checked={days.includes(day)} onChange={() => toggleDay(day)} />
                            {WEEKDAY_FULL_LABEL[day]}
                        </label>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

// Shared by the read-only Modes cell and the multi-select trigger below, so both show the exact
// same preview: up to 7 mode icons, then a "+N more" instead of letting the row grow unbounded.
const MAX_VISIBLE_MODE_ICONS = 7;

const renderModeIcons = (selectedValues, options) => {
    const selectedOptions = (selectedValues || [])
        .map(value => options.find(o => o.value === value))
        .filter(Boolean);

    if (selectedOptions.length === 0) {
        return <span className="mode-icon-row mode-icon-row--empty">Select modes</span>;
    }

    const visible = selectedOptions.slice(0, MAX_VISIBLE_MODE_ICONS);
    const extraCount = selectedOptions.length - visible.length;

    return (
        <span className="mode-icon-row">
            {visible.map(opt => (
                <ReactSVG key={opt.value} className="icon" src={opt.icon} title={opt.label} />
            ))}
            {extraCount > 0 && <span className="mode-icon-more">+{extraCount} more</span>}
        </span>
    );
};

// Mode management's Modes field — a multi-select checklist (a rule can apply to several modes
// at once), unlike the single-select FilterSelect/RepeatDaySelect above. The closed trigger shows
// the same icon-row preview as the read-only cell (see renderModeIcons) instead of plain text.
const ModeMultiSelect = ({ selected, options, onChange }) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const closeMenu = (e) => {
            if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', closeMenu);
        return () => document.removeEventListener('mousedown', closeMenu);
    }, [open]);

    const handleToggleOpen = () => {
        if (!open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuStyle({ position: 'fixed', left: rect.left, minWidth: rect.width, top: rect.bottom + 4 });
        }
        setOpen(prev => !prev);
    };

    const toggleMode = (value) => {
        onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
    };

    return (
        <div
            ref={triggerRef}
            className="filter-select filter-select--interactive filter-select--compact mode-multi-select"
            onClick={handleToggleOpen}
        >
            {renderModeIcons(selected, options)}
            <ReactSVG className="filter-select-caret" src={downCaretIcon} />
            {open && menuStyle && createPortal(
                <div
                    ref={menuRef}
                    className="filter-select-menu mode-select-menu"
                    style={menuStyle}
                    onClick={(e) => e.stopPropagation()}
                >
                    {options.map(opt => (
                        <label key={opt.value} className="repeat-day-option mode-option">
                            <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggleMode(opt.value)} />
                            <ReactSVG className="icon" src={opt.icon} />
                            {opt.label}
                        </label>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

const isHttpsUrl = (value) => /^https:\/\/.+/i.test((value || '').trim());

// The draft row's URL field is always just the globe icon (same as the read-only row) — clicking
// it opens this small popover to type/edit the URL instead of showing a raw input in the table
// cell. Same portaled/floating approach as the other dropdowns in this file.
const UrlFieldPopover = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const [draftUrl, setDraftUrl] = useState('');
    const [menuStyle, setMenuStyle] = useState(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const closeMenu = (e) => {
            if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', closeMenu);
        return () => document.removeEventListener('mousedown', closeMenu);
    }, [open]);

    const handleOpen = () => {
        setDraftUrl(value || 'https://');
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuStyle({ position: 'fixed', left: rect.left, top: rect.bottom + 4 });
        }
        setOpen(true);
    };

    const isValid = isHttpsUrl(draftUrl);

    const handleConfirm = () => {
        if (!isValid) return;
        onChange(draftUrl.trim());
        setOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleConfirm();
        if (e.key === 'Escape') setOpen(false);
    };

    return (
        <>
            <button type="button" ref={triggerRef} className="row-action-btn url-popover-trigger" onClick={handleOpen}>
                <ReactSVG className="icon" src={globeIcon} />
            </button>
            {open && menuStyle && createPortal(
                <div ref={menuRef} className="url-popover" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                    <div className="url-popover-title">3rd party map URL:</div>
                    <input
                        type="text"
                        className="rule-edit-input url-popover-input"
                        value={draftUrl}
                        onChange={e => setDraftUrl(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoFocus
                    />
                    <p className="url-popover-hint">
                        Only secure (https) URLs are accepted. Some third-party content may not render correctly, or at all, due to their own usage or security restrictions.
                    </p>
                    <div className="url-popover-actions">
                        <button type="button" className="url-popover-btn url-popover-btn--cancel" onClick={() => setOpen(false)}>
                            Cancel
                        </button>
                        <button type="button" className="url-popover-btn url-popover-btn--ok" disabled={!isValid} onClick={handleConfirm}>
                            Ok
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

const TYPE_PILL_LABEL = { dated: 'Dated', continuous: 'Continuous', expired: 'Expired' };

const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
];

// "Active" here maps onto getRuleTypeCategory's 'continuous' category — same wording distinction
// as the Type pill (Continuous rules are always currently active).
const RULE_TYPE_FILTER_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'continuous', label: 'Active' },
    { value: 'dated', label: 'Dated' },
    { value: 'expired', label: 'Expired' },
];

// "All" shows every rule regardless of direction; the other three each match only rules stored
// with exactly that direction ("Both directions" = rules that apply to both, not a catch-all).
const DIRECTION_FILTER_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'both', label: 'Both directions', icon: bothDirectionsIcon },
    { value: 'external', label: 'Entering this Hub', icon: externalDirectionIcon },
    { value: 'internal', label: 'Leaving this Hub', icon: internalDirectionIcon },
];

// Same options/icons/text as the filter above, but keyed the way a rule's `direction` field (and
// the create/edit draft) actually stores "both" — as '', not 'both'.
const DIRECTION_DRAFT_OPTIONS = [
    { value: '', label: 'Both directions', icon: bothDirectionsIcon },
    { value: 'external', label: 'Entering this Hub', icon: externalDirectionIcon },
    { value: 'internal', label: 'Leaving this Hub', icon: internalDirectionIcon },
];

// The Mode management tab's options — values match the raw strings ALL_MODES already uses
// elsewhere for this rule type (see SublocationRules.js), so existing saved rules still resolve
// to the right icon/label here.
export const MODE_OPTIONS = [
    { value: 'walk', label: 'Walk', icon: walkModeIcon },
    { value: 'car', label: 'Drive and Park', icon: carModeIcon },
    { value: 'rideHailing', label: 'Ridehail', icon: ridehailModeIcon },
    { value: 'shuttle', label: 'Shuttle', icon: shuttleModeIcon },
    { value: 'bus', label: 'Bus', icon: busModeIcon },
    { value: 'subway', label: 'Subway', icon: subwayModeIcon },
    { value: 'train', label: 'Train', icon: trainModeIcon },
    { value: 'tram', label: 'Tram', icon: tramModeIcon },
    { value: 'ferry', label: 'Ferry', icon: ferryModeIcon },
    { value: 'bike', label: 'Bike', icon: bikeModeIcon },
    { value: 'bikeshare', label: 'Bikeshare', icon: bikeshareModeIcon },
    { value: 'scooter', label: 'Scooter', icon: scooterModeIcon },
];

const SublocationRuleTypeManager = ({
    title, description, rules, ruleType, sublocationId, playlistId,
    onStageCreate, onStageDelete, onDiscardPendingCreate, onStageUpdate, onEditPendingCreate,
    // Most tabs (Label management) just store config.value as a plain string. A tab that also
    // needs a second field (Wayfinding management stores config.value as { label, value }, where
    // `label` is the Label column's text and `value` is a URL edited via UrlFieldPopover) passes
    // this to render an extra column — leaving it unset keeps the plain-string behavior unchanged.
    secondaryValueFieldLabel,
    // Header text for the primary value column — defaults to "Label" (Label management); Mode
    // management passes "Modes".
    valueFieldLabel = 'Label',
    // When set (Mode management passes MODE_OPTIONS), the primary column becomes a single-select
    // icon dropdown instead of a free-text input — still stores/reads config.value as plain text,
    // just picked from this fixed list instead of typed.
    modeOptions,
}) => {
    // "Rule status" filters the table (and the calendar highlights below) by isActive.
    const [statusFilter, setStatusFilter] = useState('all');
    const statusFilterLabel = STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter)?.label ?? 'All';

    // "Travel direction" filters by direction — null/falsy means "both directions".
    const [directionFilter, setDirectionFilter] = useState('all');
    const selectedDirectionOption = DIRECTION_FILTER_OPTIONS.find(o => o.value === directionFilter);
    const directionFilterLabel = selectedDirectionOption?.label ?? 'All';

    // "Rule type" filters by the same Active/Dated/Expired category shown on the Type pill.
    const [ruleTypeFilter, setRuleTypeFilter] = useState('all');
    const ruleTypeFilterLabel = RULE_TYPE_FILTER_OPTIONS.find(o => o.value === ruleTypeFilter)?.label ?? 'All';

    const today = new Date();
    const todayParts = { year: today.getFullYear(), month: today.getMonth(), day: today.getDate() };

    // Clicking a day on either calendar below filters the table to whatever applies on that
    // date; clicking the same day again (or the chip's clear button) clears it.
    const [selectedDate, setSelectedDate] = useState(null);
    const handleDayClick = (year, month, day) => {
        setSelectedDate(prev => (prev && prev.year === year && prev.month === month && prev.day === day)
            ? null
            : { year, month, day });
    };

    // The calendars' own highlights should keep showing every rule that matches the other
    // filters, even once a date is selected — only the table below narrows further by date, so
    // there's still a full picture to click a different date from.
    const highlightRules = (rules || [])
        .filter(r => r.type === ruleType && r.sublocationId === sublocationId)
        .filter(r => statusFilter === 'all' || (statusFilter === 'active' ? !!r.isActive : !r.isActive))
        .filter(r => directionFilter === 'all'
            || (directionFilter === 'both' ? !r.direction : r.direction === directionFilter))
        .filter(r => ruleTypeFilter === 'all' || getRuleTypeCategory(r, todayParts) === ruleTypeFilter);

    const typeRules = highlightRules.filter(r => !selectedDate || isDatedRuleCurrentlyInRange(r, selectedDate));

    // Column header sorting — three clicks cycle a column through ascending, descending, then
    // back to unsorted; clicking a different column starts that one fresh at ascending.
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    const handleSortClick = (column) => {
        if (sortColumn !== column) {
            setSortColumn(column);
            setSortDirection('asc');
        } else if (sortDirection === 'asc') {
            setSortDirection('desc');
        } else {
            setSortColumn(null);
        }
    };

    // A missing date/time is treated as the "smallest" possible value — same rule a real value
    // would follow, just with nothing to compare it against. That means it naturally sorts first
    // ascending and last descending, instead of being pinned to one end regardless of direction.
    const compareNullableDateParts = (a, b, dir) => {
        if (!a && !b) return 0;
        if (!a) return -dir;
        if (!b) return dir;
        return compareDateParts(a, b) * dir;
    };

    const compareNullableTime = (a, b, dir) => {
        if (!a && !b) return 0;
        if (!a) return -dir;
        if (!b) return dir;
        return (a < b ? -1 : a > b ? 1 : 0) * dir;
    };

    const compareRulesByColumn = (a, b, column, direction) => {
        const dir = direction === 'desc' ? -1 : 1;
        switch (column) {
            case 'active':
                // Ascending = Active first, descending = Inactive first (not just A-Z on a label).
                return ((a.isActive ? 0 : 1) - (b.isActive ? 0 : 1)) * dir;
            case 'type': {
                // A fixed, meaningful order (Continuous, Dated, Expired) — not alphabetical.
                const rank = { continuous: 0, dated: 1, expired: 2 };
                return (rank[getRuleTypeCategory(a, todayParts)] - rank[getRuleTypeCategory(b, todayParts)]) * dir;
            }
            case 'turnOnDate':
                return compareNullableDateParts(parseDateParts(a.dateFrom), parseDateParts(b.dateFrom), dir);
            case 'turnOffDate':
                return compareNullableDateParts(parseDateParts(a.dateTo), parseDateParts(b.dateTo), dir);
            case 'turnOnTime':
                return compareNullableTime(a.timeFrom, b.timeFrom, dir);
            case 'turnOffTime':
                return compareNullableTime(a.timeTo, b.timeTo, dir);
            default:
                return 0;
        }
    };

    // Array.prototype.sort is stable (guaranteed since ES2019), so rules that tie on the sorted
    // column keep their existing relative order instead of jumping around.
    const sortedTypeRules = sortColumn
        ? [...typeRules].sort((a, b) => compareRulesByColumn(a, b, sortColumn, sortDirection))
        : typeRules;

    // Bolds + arrows the header when this is the active sort column; clicking it cycles through
    // handleSortClick's asc → desc → unsorted states.
    const renderSortableHeader = (label, column, className) => {
        const isSorted = sortColumn === column;
        return (
            <th
                className={`${className} sortable-header${isSorted ? ' sortable-header--active' : ''}`}
                onClick={() => handleSortClick(column)}
            >
                {label}
                {isSorted && <span className="sort-arrow">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
            </th>
        );
    };

    // Starts on the current month; the nav arrows shift this by whole months from there.
    const [monthOffset, setMonthOffset] = useState(0);
    const baseDate = new Date();
    baseDate.setDate(1);
    baseDate.setMonth(baseDate.getMonth() + monthOffset);

    const leftYear = baseDate.getFullYear();
    const leftMonth = baseDate.getMonth();
    const rightMonth = (leftMonth + 1) % 12;
    const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;

    // The row shown while creating a new rule, or editing an existing one — null when neither is
    // happening. Confirming it only stages the change locally; it isn't actually written to biz
    // until the modal's own Save is clicked, same as every other field in this modal.
    const [draft, setDraft] = useState(null);
    // Non-null while `draft` represents an edit of an existing row rather than a brand new one —
    // holds that row's id so the edit renders in place instead of as a new row up top.
    const [editingRuleId, setEditingRuleId] = useState(null);

    const handleDraftField = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

    // A TIME column can come back from biz with seconds ("13:00:00") and a DATE column with a
    // full ISO stamp — the native date/time inputs below only want "YYYY-MM-DD"/"HH:MM".
    // Same normalization used both to seed the draft when editing starts and, at confirm time,
    // to check whether the user actually changed anything — comparing against a value built this
    // same way avoids false positives from format differences (e.g. biz's "13:00:00" vs the plain
    // "13:00" the time input and draft state use).
    const ruleToDraft = (rule) => ({
        isActive: !!rule.isActive,
        dateFrom: rule.dateFrom ? rule.dateFrom.slice(0, 10) : '',
        dateTo: rule.dateTo ? rule.dateTo.slice(0, 10) : '',
        timeFrom: rule.timeFrom ? rule.timeFrom.slice(0, 5) : '',
        timeTo: rule.timeTo ? rule.timeTo.slice(0, 5) : '',
        days: rule.days && rule.days.length > 0 ? rule.days : [...WEEKDAY_ORDER],
        direction: rule.direction || '',
        // Mode management stores config.value as an array of mode strings (multi-select);
        // every other tab stores it as a plain string (or { label, value } for Wayfinding).
        label: modeOptions
            ? (Array.isArray(rule.config?.value) ? rule.config.value : [])
            : String(secondaryValueFieldLabel ? (rule.config?.value?.label ?? '') : (rule.config?.value ?? '')),
        secondaryValue: secondaryValueFieldLabel ? String(rule.config?.value?.value ?? '') : '',
    });

    const handleStartEdit = (rule) => {
        setEditingRuleId(rule.id);
        setDraft(ruleToDraft(rule));
    };

    // draft.label is an array of selected modes for Mode management, otherwise a plain string.
    const isDraftLabelEmpty = () => (modeOptions ? draft.label.length === 0 : !draft.label.trim());

    // Order-insensitive — toggling a checkbox off then back on shouldn't count as a change just
    // because it moved to the end of the array.
    const arraysEqualAsSets = (a, b) => {
        const setA = new Set(a || []);
        const setB = new Set(b || []);
        if (setA.size !== setB.size) return false;
        for (const value of setA) if (!setB.has(value)) return false;
        return true;
    };

    // Compares the live draft against what editing this rule started from, so confirming without
    // actually touching anything doesn't stage a no-op update (which would otherwise light up
    // Save/Revert as if a real edit had been made).
    const isDraftUnchangedFrom = (baseline) => (
        draft.isActive === baseline.isActive
        && draft.dateFrom === baseline.dateFrom
        && draft.dateTo === baseline.dateTo
        && draft.timeFrom === baseline.timeFrom
        && draft.timeTo === baseline.timeTo
        && draft.direction === baseline.direction
        && arraysEqualAsSets(draft.days, baseline.days)
        && (modeOptions ? arraysEqualAsSets(draft.label, baseline.label) : draft.label === baseline.label)
        && draft.secondaryValue === baseline.secondaryValue
    );

    const handleConfirmDraft = () => {
        if (isDraftLabelEmpty() || (secondaryValueFieldLabel && !draft.secondaryValue.trim())) return;

        const editedFields = {
            config: {
                value: modeOptions
                    ? draft.label
                    : (secondaryValueFieldLabel
                        ? { label: draft.label.trim(), value: draft.secondaryValue.trim() }
                        : draft.label.trim()),
            },
            isActive: draft.isActive,
            dateFrom: draft.dateFrom || null,
            dateTo: draft.dateTo || null,
            timeFrom: draft.timeFrom || null,
            timeTo: draft.timeTo || null,
            direction: draft.direction || null,
            days: draft.days.length === 7 ? null : draft.days,
        };

        if (editingRuleId) {
            const editingRule = typeRules.find(r => r.id === editingRuleId);
            if (editingRule?.__pending) {
                onEditPendingCreate(editingRuleId, editedFields);
            } else if (!editingRule || !isDraftUnchangedFrom(ruleToDraft(editingRule))) {
                // Only stage an update if something was actually touched — confirming an edit
                // with no real changes shouldn't mark the modal as having unsaved changes.
                onStageUpdate(editingRuleId, editedFields);
            }
        } else {
            onStageCreate({
                ...NEW_RULE_BASE_FIELDS,
                ...editedFields,
                type: ruleType,
                sublocationId,
                playlistId,
            });
        }

        setDraft(null);
        setEditingRuleId(null);
    };

    const handleCancelDraft = () => {
        setDraft(null);
        setEditingRuleId(null);
    };

    // A row still pending creation (never hit biz) just gets dropped from the staged list; a
    // real, already-saved row gets staged for deletion on the next Save instead of deleted now.
    const handleDelete = (rule) => {
        if (rule.__pending) {
            onDiscardPendingCreate(rule.id);
        } else {
            onStageDelete(rule.id);
        }
    };

    // Shared by both the "create a new rule" row (rendered at the top) and an in-place row edit —
    // same fields either way, just staged differently on confirm (see handleConfirmDraft above).
    const renderDraftRow = (key) => (
        <tr key={key}>
            <td className="col-active">
                <Switch
                    checked={draft.isActive}
                    onChange={val => handleDraftField('isActive', val)}
                    onColor="#2386EE"
                    offColor="#CCD2D3"
                    uncheckedIcon={false}
                    checkedIcon={false}
                    height={22}
                    width={44}
                />
            </td>
            <td className="col-type">
                <span className="type-pill type-pill--pending">-</span>
            </td>
            <td className="col-turn">
                <input
                    type="date"
                    className="rule-edit-input"
                    value={draft.dateFrom}
                    onChange={e => handleDraftField('dateFrom', e.target.value)}
                />
            </td>
            <td className="col-time">
                <input
                    type="time"
                    className="rule-edit-input"
                    value={draft.timeFrom}
                    onChange={e => handleDraftField('timeFrom', e.target.value)}
                />
            </td>
            <td className="col-turn">
                <input
                    type="date"
                    className="rule-edit-input"
                    value={draft.dateTo}
                    onChange={e => handleDraftField('dateTo', e.target.value)}
                />
            </td>
            <td className="col-time">
                <input
                    type="time"
                    className="rule-edit-input"
                    value={draft.timeTo}
                    onChange={e => handleDraftField('timeTo', e.target.value)}
                />
            </td>
            <td className="col-repeat">
                <RepeatDaySelect
                    compact
                    days={draft.days}
                    onChange={val => handleDraftField('days', val)}
                />
            </td>
            <td className="travel-col">
                <FilterSelect
                    compact
                    icon={DIRECTION_DRAFT_OPTIONS.find(o => o.value === draft.direction)?.icon}
                    value={DIRECTION_DRAFT_OPTIONS.find(o => o.value === draft.direction)?.label ?? 'Both directions'}
                    options={DIRECTION_DRAFT_OPTIONS}
                    selectedValue={draft.direction}
                    onChange={val => handleDraftField('direction', val)}
                />
            </td>
            {secondaryValueFieldLabel && (
                <td className="secondary-value-col">
                    <UrlFieldPopover
                        value={draft.secondaryValue}
                        onChange={val => handleDraftField('secondaryValue', val)}
                    />
                </td>
            )}
            <td className={`label-col${modeOptions ? ' mode-icons-col' : ''}${!secondaryValueFieldLabel ? ' label-col--divided' : ''}`}>
                {modeOptions ? (
                    <ModeMultiSelect
                        selected={draft.label}
                        options={modeOptions}
                        onChange={val => handleDraftField('label', val)}
                    />
                ) : (
                    <input
                        type="text"
                        className="rule-edit-input"
                        placeholder="e.g. VIP Lot D7 South"
                        value={draft.label}
                        onChange={e => handleDraftField('label', e.target.value)}
                    />
                )}
            </td>
            <td className="actions-col">
                <button
                    className="row-action-btn row-action-btn--confirm"
                    type="button"
                    disabled={isDraftLabelEmpty() || (secondaryValueFieldLabel && !draft.secondaryValue.trim())}
                    onClick={handleConfirmDraft}
                >
                    <ReactSVG className="icon" src={checkIcon} />
                </button>
                <button
                    className="row-action-btn row-action-btn--cancel"
                    type="button"
                    onClick={handleCancelDraft}
                >
                    <ReactSVG className="icon" src={trashIcon} />
                </button>
            </td>
        </tr>
    );

    return (
        <div className="rule-type-manager">
            <div className="rule-type-top">
                <div className="rule-type-column">
                    <h3 className="rule-type-title">{title}</h3>
                    <p className="section-description rule-type-description">{description}</p>

                    <div className="rule-type-filters">
                        <h3 className="rule-type-subtitle">Filters</h3>
                        <div className="filter-row">
                            <label className="form-label">Travel direction:</label>
                            <FilterSelect
                                icon={selectedDirectionOption?.icon}
                                value={directionFilterLabel}
                                options={DIRECTION_FILTER_OPTIONS}
                                selectedValue={directionFilter}
                                onChange={setDirectionFilter}
                            />
                        </div>
                        <div className="filter-row">
                            <label className="form-label">Rule status:</label>
                            <FilterSelect
                                value={statusFilterLabel}
                                options={STATUS_FILTER_OPTIONS}
                                selectedValue={statusFilter}
                                onChange={setStatusFilter}
                            />
                        </div>
                        <div className="filter-row">
                            <label className="form-label">Rule type:</label>
                            <FilterSelect
                                value={ruleTypeFilterLabel}
                                options={RULE_TYPE_FILTER_OPTIONS}
                                selectedValue={ruleTypeFilter}
                                onChange={setRuleTypeFilter}
                            />
                        </div>
                    </div>
                </div>

                <div className="rule-type-column">
                    <h3 className="rule-type-subtitle">Rules calendar</h3>
                    <p className="section-description">
                        This calendar represents the dates where rules exist. Selecting any date or range will show rules that apply to any dates within that timeframe.
                    </p>

                    <div className="rules-calendar">
                        <button className="calendar-nav calendar-nav--prev" type="button" onClick={() => setMonthOffset(prev => prev - 1)}>
                            <ReactSVG className="icon" src={leftAngleIcon} />
                        </button>
                        <MiniCalendar
                            year={leftYear}
                            month={leftMonth}
                            highlights={buildDateHighlights(highlightRules, leftYear, leftMonth)}
                            selectedDate={selectedDate}
                            onDayClick={handleDayClick}
                        />
                        <MiniCalendar
                            year={rightYear}
                            month={rightMonth}
                            highlights={buildDateHighlights(highlightRules, rightYear, rightMonth)}
                            selectedDate={selectedDate}
                            onDayClick={handleDayClick}
                        />
                        <button className="calendar-nav calendar-nav--next" type="button" onClick={() => setMonthOffset(prev => prev + 1)}>
                            <ReactSVG className="icon" src={rightAngleIcon} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="rule-type-table-section">
                <div className="rule-type-table-header">
                    {selectedDate && (
                        <div className="date-filter-chip">
                            Showing rules for {formatDateParts(selectedDate)}
                            <button type="button" onClick={() => setSelectedDate(null)}>
                                <ReactSVG className="icon" src={closeIcon} />
                            </button>
                        </div>
                    )}
                    <button
                        className="create-rule-btn"
                        type="button"
                        disabled={!!draft}
                        onClick={() => setDraft({ ...EMPTY_DRAFT, label: modeOptions ? [] : EMPTY_DRAFT.label })}
                    >
                        Create a new rule
                        <ReactSVG className="icon" src={plusIcon} />
                    </button>
                </div>
                <div className="rule-type-table-scroll">
                    <table className="rule-type-table">
                        <thead>
                            <tr>
                                {renderSortableHeader('Active', 'active', 'col-active')}
                                {renderSortableHeader('Type', 'type', 'col-type')}
                                {renderSortableHeader('Turn on date', 'turnOnDate', 'col-turn')}
                                {renderSortableHeader('Turn on time', 'turnOnTime', 'col-time')}
                                {renderSortableHeader('Turn off date', 'turnOffDate', 'col-turn')}
                                {renderSortableHeader('Turn off time', 'turnOffTime', 'col-time')}
                                <th className="col-repeat">Repeat</th>
                                <th className="travel-col">Travel</th>
                                {secondaryValueFieldLabel && <th className="secondary-value-col">{secondaryValueFieldLabel}</th>}
                                <th className="label-col">{valueFieldLabel}</th>
                                <th className="actions-col" />
                            </tr>
                        </thead>
                        <tbody>
                            {draft && !editingRuleId && renderDraftRow('new-rule-draft')}
                            {typeRules.length === 0 && !draft ? (
                                <tr>
                                    <td className="empty-row" colSpan={secondaryValueFieldLabel ? 11 : 10}>No rules of this type yet.</td>
                                </tr>
                            ) : sortedTypeRules.map(rule => {
                                if (editingRuleId === rule.id) return renderDraftRow(rule.id);

                                const isDated = !!(rule.dateFrom || rule.dateTo);
                                const typeKey = getRuleTypeCategory(rule, todayParts);
                                return (
                                    <tr key={rule.id} className={rule.isActive ? '' : 'rule-row--inactive'}>
                                        <td className="col-active">
                                            <Switch
                                                checked={!!rule.isActive}
                                                onChange={() => {}}
                                                onColor="#2386EE"
                                                offColor="#CCD2D3"
                                                uncheckedIcon={false}
                                                checkedIcon={false}
                                                height={22}
                                                width={44}
                                            />
                                        </td>
                                        <td className="col-type">
                                            <span className={`type-pill type-pill--${typeKey}`}>{TYPE_PILL_LABEL[typeKey]}</span>
                                        </td>
                                        <td className="col-turn">{isDated ? formatTurnDate(rule.dateFrom) : '-'}</td>
                                        <td className="col-time">{formatTime(rule.timeFrom)}</td>
                                        <td className="col-turn">{isDated ? formatTurnDate(rule.dateTo) : '-'}</td>
                                        <td className="col-time">{formatTime(rule.timeTo)}</td>
                                        <td className="col-repeat">{formatRepeat(rule.days)}</td>
                                        <td className="travel-col">
                                            <ReactSVG
                                                className="travel-direction-icon"
                                                src={DIRECTION_FILTER_OPTIONS.find(o => o.value === (rule.direction || 'both')).icon}
                                            />
                                        </td>
                                        {secondaryValueFieldLabel && (
                                            <td className="secondary-value-col">
                                                {rule.config?.value?.value ? (
                                                    <a
                                                        href={rule.config.value.value}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title={rule.config.value.value}
                                                        className="secondary-value-link"
                                                    >
                                                        <ReactSVG className="icon" src={globeIcon} />
                                                    </a>
                                                ) : '-'}
                                            </td>
                                        )}
                                        <td className={`label-col${modeOptions ? ' mode-icons-col' : ''}${!secondaryValueFieldLabel ? ' label-col--divided' : ''}`}>
                                            {modeOptions ? (
                                                renderModeIcons(rule.config?.value, modeOptions)
                                            ) : (
                                                String(secondaryValueFieldLabel ? (rule.config?.value?.label ?? '') : (rule.config?.value ?? ''))
                                            )}
                                        </td>
                                        <td className="actions-col">
                                            <button
                                                className="row-action-btn"
                                                type="button"
                                                disabled={!!draft}
                                                onClick={() => handleStartEdit(rule)}
                                            >
                                                <ReactSVG className="icon" src={pencilIcon} />
                                            </button>
                                            <button
                                                className="row-action-btn"
                                                type="button"
                                                disabled={!!draft}
                                                onClick={() => handleDelete(rule)}
                                            >
                                                <ReactSVG className="icon" src={trashIcon} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SublocationRuleTypeManager;
